import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, Eye, Loader } from 'lucide-react';
import Header from '../components/Header';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image_url: string;
  author_name: string;
  published_date: string;
  view_count: number;
  meta_description: string;
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to detect YouTube URLs
  const isYouTubeUrl = (url: string): boolean => {
    if (!url) return false;
    const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    return youtubeRegex.test(url);
  };

  // Helper function to extract YouTube video ID
  const getYouTubeVideoId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
    return match ? match[1] : null;
  };

  const fetchPost = useCallback(async () => {
    if (!slug) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/blog/public/posts/${slug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Blog post not found');
        }
        throw new Error('Failed to fetch blog post');
      }
      
      const data = await response.json();
      setPost(data);
      
      // Update page title and meta description
      document.title = `${data.title} | BandCam.io Blog`;
      if (data.meta_description) {
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement('meta');
          metaDesc.setAttribute('name', 'description');
          document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', data.meta_description);
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError(err instanceof Error ? err.message : 'Failed to load blog post');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Loader className="w-12 h-12 animate-spin text-sky" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-midnight">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">
            {error === 'Blog post not found' ? 'Post Not Found' : 'Error Loading Post'}
          </h1>
          <p className="text-xl text-gray-300 mb-8">{error}</p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/blog"
              className="bg-sky text-black px-6 py-3 border-2 border-black font-semibold hover:bg-goldie transition-colors"
            >
              Back to Blog
            </Link>
            <Link
              to="/"
              className="bg-goldie text-black px-6 py-3 border-2 border-black font-semibold hover:bg-sky transition-colors"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Image/Video */}
        {post.featured_image_url && (
          <div className="mb-8 border-2 border-black overflow-hidden">
            {isYouTubeUrl(post.featured_image_url) ? (
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(post.featured_image_url)}`}
                title={post.title}
                className="w-full aspect-video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <img
                src={post.featured_image_url}
                alt={post.title}
                className="w-full aspect-video object-cover"
              />
            )}
          </div>
        )}

        {/* Post Header */}
        <div className="bg-sky border-2 border-black p-8 mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-black mb-6">
            {post.title}
          </h1>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-6 text-gray-700">
            <div className="flex items-center">
              <User className="w-5 h-5 mr-2" />
              <span className="font-semibold">{post.author_name}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              <span>{formatDate(post.published_date)}</span>
            </div>
            <div className="flex items-center">
              <Eye className="w-5 h-5 mr-2" />
              <span>{post.view_count} views</span>
            </div>
          </div>
        </div>

        {/* Post Content */}
        <div className="bg-white border-2 border-black p-8 md:p-12 text-black">
          <div 
            className="prose prose-lg max-w-none text-black
              prose-headings:text-black prose-headings:font-bold
              prose-h2:text-3xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-2xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-gray-800 prose-p:mb-4 prose-p:leading-relaxed
              prose-a:text-blue-600 prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
              prose-strong:text-black prose-strong:font-bold
              prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
              prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
              prose-li:text-gray-800 prose-li:mb-2
              prose-img:border-2 prose-img:border-black prose-img:my-6
              prose-blockquote:border-l-4 prose-blockquote:border-goldie prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-800"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* Call to Action */}
        <div className="mt-12 bg-goldie border-2 border-black p-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-4">
            Ready to bring BandCam to your school?
          </h2>
          <p className="text-lg text-gray-800 mb-6">
            Learn more about our services and how we can help capture your band's best moments.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/band-directors"
              className="bg-black text-white px-8 py-3 font-bold hover:bg-gray-800 transition-colors"
            >
              For Band Directors
            </Link>
            <Link
              to="/contact"
              className="bg-sky text-black px-8 py-3 border-2 border-black font-bold hover:bg-white transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>

      </article>

      {/* Footer */}
      <footer className="bg-sky border-t-2 border-black mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-black">
            <Link to="/" className="underline hover:text-gray-700 font-semibold">
              Home
            </Link>
            {' | '}
            <Link to="/about" className="underline hover:text-gray-700 font-semibold">
              About Us
            </Link>
            {' | '}
            <Link to="/contact" className="underline hover:text-gray-700 font-semibold">
              Contact Us
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;

